import { fetchIndicatorDetail } from 'redux/IndicatorPageSlice';

import { resolveDimFilter } from './planPageFetchHelpers';

import type { IndicatorDriver } from 'models/response/driver.interface';
import type { AppDispatch } from 'utils/GlobalHelpers';

type DimensionFilterEntry = Record<string, string[]>;

interface BlockDimensionsLike {
  dimensions?: Array<{ name: string; items?: string[] }>;
}

interface IndicatorTableDataLike {
  column_headers?: Array<{ id?: number | string; is_dimension?: boolean }>;
  indicator_id?: number | string;
}

interface FetchPlanPageIndicatorChartDetailsOptions {
  dispatch: AppDispatch;
  /** Selected indicator driver from plan page (not indicator detail API payload). */
  currentDriver: IndicatorDriver;
  scenarioId: number;
  dimId: number | string | null;
  appliedFilters: DimensionFilterEntry[] | null | undefined;
  blockDimensions: BlockDimensionsLike | null | undefined;
}

/**
 * Chart ``dim_id`` from the loaded table when available; otherwise the driver's first
 * input dimension so ``/details`` can fire in parallel with ``/data_input`` on panel open.
 */
export const resolveIndicatorChartDimId = (
  currentDriver: IndicatorDriver,
  tableData?: IndicatorTableDataLike | null,
): number | string | null => {
  if (
    tableData?.column_headers?.length &&
    Number(tableData.indicator_id) === Number(currentDriver.id)
  ) {
    const header = tableData.column_headers[0];
    if (header?.is_dimension) {
      return header.id ?? null;
    }
  }

  return currentDriver.dimensions?.[0] ?? null;
};

/** Stable signature for chart ``/indicator/:id/details`` requests driven by plan filters. */
export const buildIndicatorChartDetailsSignature = ({
  currentDriver,
  scenarioId,
  dimId,
  appliedFilters,
  blockDimensions,
}: FetchPlanPageIndicatorChartDetailsOptions): string =>
  JSON.stringify({
    indicatorId: currentDriver.id,
    scenarioId,
    dimId,
    filterDimItems: resolveDimFilter(appliedFilters, blockDimensions),
  });

type ChartDetailsSchedulerState = {
  timerId: ReturnType<typeof setTimeout> | null;
  lastSignature: string | null;
};

const chartDetailsScheduler: ChartDetailsSchedulerState = {
  timerId: null,
  lastSignature: null,
};

/** Clears pending chart-detail fetches (e.g. when navigating away from the input panel). */
export const resetPlanPageIndicatorChartFetchScheduler = () => {
  if (chartDetailsScheduler.timerId) {
    clearTimeout(chartDetailsScheduler.timerId);
    chartDetailsScheduler.timerId = null;
  }
  chartDetailsScheduler.lastSignature = null;
};

interface DispatchChartDetailsOptions {
  force?: boolean;
}

const dispatchIndicatorChartDetailsIfNeeded = (
  options: FetchPlanPageIndicatorChartDetailsOptions,
  { force = false }: DispatchChartDetailsOptions = {},
) => {
  const signature = buildIndicatorChartDetailsSignature(options);
  if (!force && signature === chartDetailsScheduler.lastSignature) {
    return false;
  }

  chartDetailsScheduler.lastSignature = signature;

  const activeSubsets = resolveDimFilter(options.appliedFilters, options.blockDimensions);
  const parameters: Record<string, unknown> = {
    dim_id: options.dimId,
    scenario_id: options.scenarioId,
    is_chart_show: true,
  };

  if (activeSubsets.length > 0) {
    parameters.filter_dim_items = JSON.stringify(activeSubsets);
  }

  options.dispatch(
    fetchIndicatorDetail({
      currentDriver: options.currentDriver,
      parameters,
    }),
  );

  return true;
};

/**
 * Fetches chart ``/indicator/:id/details`` immediately (no debounce).
 * Used when the input panel opens so the chart loads in parallel with ``/data_input``.
 */
export const fetchPlanPageIndicatorChartDetailsNow = (
  options: FetchPlanPageIndicatorChartDetailsOptions,
  dispatchOptions: DispatchChartDetailsOptions = { force: true },
) => {
  if (chartDetailsScheduler.timerId) {
    clearTimeout(chartDetailsScheduler.timerId);
    chartDetailsScheduler.timerId = null;
  }

  dispatchIndicatorChartDetailsIfNeeded(options, dispatchOptions);
};

interface ScheduleIndicatorChartDetailsOptions {
  delayMs?: number;
  force?: boolean;
}

/**
 * Debounces and dedupes filter-driven chart ``/indicator/:id/details`` refetches.
 * Initial panel open uses ``fetchPlanPageIndicatorChartDetailsNow`` instead.
 */
export const schedulePlanPageIndicatorChartDetailsFetch = (
  options: FetchPlanPageIndicatorChartDetailsOptions,
  scheduleOptions: ScheduleIndicatorChartDetailsOptions = {},
) => {
  const { delayMs = 150, force = false } = scheduleOptions;

  if (chartDetailsScheduler.timerId) {
    clearTimeout(chartDetailsScheduler.timerId);
  }

  chartDetailsScheduler.timerId = setTimeout(() => {
    chartDetailsScheduler.timerId = null;
    dispatchIndicatorChartDetailsIfNeeded(options, { force });
  }, delayMs);
};
